import React, { ReactNode } from 'react'
import {
  forwardRef,
  Button,
  ButtonGroup,
  ButtonGroupProps,
  Flex,
  Menu,
  MenuItem,
  MenuList,
  useBreakpointValue,
  MenuButton
} from '../../chakra'
import { ChevronDown } from '~/icons'
import { Msg } from '~/common/localizationUtils'
import useGroupChildren from '~/common/childrenUtils'
import { forceArray } from '~/common/arrayUtils'

/**
 * Button Group Actions
 *
 * Horizontally aligns a series of action buttons together as a group. Only one primary
 * button is expected in a button group. The spacing between buttons will default to 16px.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-button-groups--docs
 */
export const ButtonGroupActions = forwardRef(({ size = 'md', children, ...props }: ButtonGroupProps, ref) => (
  <ButtonGroup spacing={size} size={size} ref={ref} {...props}>
    {children}
  </ButtonGroup>
))

ButtonGroupActions.displayName = 'ButtonGroupActions'

/**
 * @deprecated DO NOT USE (YET).  These are experimental components that
 * are not yet ready for production use (only used in SOW right now)
 */
export const ResponsiveButtonGroup = ({
  children,
  forcedIntoOverflowMenu = [],
  overflowOver = { base: 1, sm: 3 },
  dataTestId = 'responsive-button-group'
}: {
  children: ReactNode
  forcedIntoOverflowMenu?: { dataTestId?: string; label: string; onClickHandler: () => void }[]
  overflowOver?: Partial<Record<string, number>>
  dataTestId?: string
}) => {
  const overflowLimit = useBreakpointValue(overflowOver) || 3
  const extracted = useGroupChildren(React.Children.toArray(children), {
    buttons: {
      isComponent: Button,
      max: 10
    },
    rest: {
      max: 10
    }
  })
  const buttons = forceArray(extracted.buttons)
  const allowedChildren = buttons.slice(0, overflowLimit)
  const overflowed = buttons.slice(overflowLimit)

  return (
    <Flex columnGap={4} rowGap={2} flexWrap="wrap">
      {extracted.rest}
      {allowedChildren}
      {(forcedIntoOverflowMenu.length > 0 || overflowed.length > 0) && (
        <Menu>
          <MenuButton as={Button} rightIcon={<ChevronDown />} data-testid={dataTestId}>
            <Msg id="common.ui.actions" />
          </MenuButton>
          <MenuList>
            {/* The are buttons that have been overflowed into this menu due to page width */}
            {overflowed.map(item => (
              <MenuItem {...item.props} />
            ))}
            {/* These have been passed in by the dev and show up last in the list */}
            {forcedIntoOverflowMenu.map(item => (
              <MenuItem data-testid={item?.dataTestId} onClick={item.onClickHandler}>
                {item.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      )}
    </Flex>
  )
}
